import {
  Calculator,
  Sparkles,
  Plane,
  CreditCard,
  ArrowRight,
  Globe2,
  TrendingUp,
  Wallet,
  BadgeDollarSign,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const features = [
  {
    icon: Calculator,
    title: "Reward Value Calculator",
    slug: "reward-value-calculator",
    description:
      "Estimate the potential travel value of your airline miles and reward points across different redemption options.",
    details:
      "This tool helps you calculate the real-world value of your reward points based on airline partners, cash value, and travel class upgrades.",
  },
  {
    icon: Sparkles,
    title: "Smart Redemption Suggestions",
    slug: "smart-redemption-suggestions",
    description:
      "Discover estimated redemption opportunities based on your points balance, travel preferences, and destination.",
    details:
      "Get AI-style suggestions for the best way to redeem your points based on current airline deals and transfer partners.",
  },
  {
    icon: Plane,
    title: "Premium Cabin Opportunities",
    slug: "premium-cabin-opportunities",
    description:
      "Explore potential business and first-class travel opportunities using reward points and transfer partners.",
    details:
      "Find hidden premium cabin deals where your points unlock luxury travel experiences at the lowest cost.",
  },
  {
    icon: CreditCard,
    title: "Multi-Card Support",
    slug: "multi-card-support",
    description:
      "Compare redemption possibilities across major US reward programs including travel and airline loyalty cards.",
    details:
      "Connect multiple credit cards and compare which one gives maximum reward value per booking.",
  },
  {
    icon: Globe2,
    title: "Destination Reward Analysis",
    slug: "destination-reward-analysis",
    description:
      "Analyze estimated reward value between departure cities and international travel destinations.",
    details:
      "See which destinations give the highest value per point depending on season and demand.",
  },
  {
    icon: TrendingUp,
    title: "Transfer Partner Insights",
    slug: "transfer-partner-insights",
    description:
      "Explore potential airline transfer opportunities and compare estimated travel reward value.",
    details:
      "Understand which airline partners give the best transfer ratio for your credit card points.",
  },
  {
    icon: Wallet,
    title: "Flexible Reward Comparisons",
    slug: "flexible-reward-comparisons",
    description:
      "Compare cashback, travel rewards, gift cards, and airline transfer redemption opportunities.",
    details:
      "Evaluate all redemption options side-by-side to maximize your financial benefit.",
  },
  {
    icon: BadgeDollarSign,
    title: "Estimated Point Value Tracking",
    slug: "estimated-point-value-tracking",
    description:
      "Understand estimated cents-per-point value across multiple redemption categories and travel programs.",
    details:
      "Track how your point value changes over time and optimize redemption timing.",
  },
];

const FeaturesSection = () => {
  const navigate = useNavigate();
  const [selectedFeature, setSelectedFeature] = useState(null);

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white via-[#f8fafc] to-[#eef4ff] py-24 px-6 lg:px-16">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-72 h-72 bg-yellow-300/20 blur-3xl rounded-full animate-blob"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-300/20 blur-3xl rounded-full animate-blob"></div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(15,23,42,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />

        <svg
          className="absolute inset-0 w-full h-full opacity-10"
          viewBox="0 0 1400 800"
          fill="none"
        >
          <path
            d="M100 600 C300 200, 900 100, 1250 320"
            stroke="#D4AF37"
            strokeWidth="2"
            strokeDasharray="10 10"
            className="animate-route"
          />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm text-[11px] font-black uppercase tracking-[0.2em] text-[#D4AF37] mb-8">
            <Sparkles size={14} />
            Powerful Reward Features
          </div>

          <h2 className="text-5xl lg:text-6xl font-black text-slate-900 leading-tight tracking-tight">
            Everything You Need To
            <span className="block text-[#D4AF37]">Maximize Reward Points</span>
          </h2>

          {/* Description */}
          <p className="text-slate-600 text-lg leading-relaxed mt-8 max-w-3xl mx-auto">
            Explore estimated redemption opportunities, compare travel reward
            value, and discover smarter ways to use airline miles and credit
            card points across major US loyalty programs.
          </p>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-8 mt-20">
          {features?.map((feature, index) => {
            const Icon = feature.icon;

            return (
              <div
                key={index}
                // onClick={() => navigate(`/features/${feature.slug}`)}
                className="group relative bg-white border rounded-3xl p-8 shadow hover:shadow-2xl transition-all cursor-pointer"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-yellow-100 rounded-xl mb-5">
                  <Icon className="text-[#D4AF37]" size={26} />
                </div>

                <h3 className="text-xl font-bold">{feature.title}</h3>

                <p className="text-sm text-slate-600 mt-3">
                  {feature.description}
                </p>

                {/* <div className="flex items-center gap-2 mt-6 text-[#D4AF37] font-bold text-sm">
                  Explore Feature
                  <ArrowRight size={16} />
                </div> */}
              </div>
            );
          })}
        </div>

        <div className="mt-24 rounded-[2.5rem] bg-gradient-to-r from-[#0f172a] to-[#111827] overflow-hidden relative px-10 py-16">
          <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-400/10 blur-3xl rounded-full"></div>

          <div className="relative z-10 max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/10 text-[11px] font-black uppercase tracking-[0.2em] text-[#D4AF37] mb-8">
              <Sparkles size={14} />
              Reward Intelligence
            </div>

            <h3 className="text-4xl lg:text-5xl font-black text-white leading-tight">
              Start Exploring Smarter Reward Opportunities
            </h3>

            <p className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
              Compare estimated reward value, explore premium travel
              opportunities, and discover smarter ways to maximize your airline
              miles and credit card points.
            </p>

            <p className="text-slate-500 text-sm mt-8 max-w-2xl">
              Redemption estimates are based on publicly available travel
              pricing, historical trends, and transfer partner availability.
              Actual reward value may vary depending on booking dates and
              airline inventory.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
